import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Smartphone, MapPin, Navigation, Camera, CheckCircle2, AlertTriangle,
  Package, Truck, ScanLine, ArrowRight, WifiOff, Wifi, RefreshCw, Check,
} from "lucide-react";
import api from "@/lib/api";
import { useFetch, Spinner } from "@/components/portal/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { QrScanner } from "@/components/QrScanner";

const QKEY = "ebik_driver_queue";
const readQ = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQ = (q) => localStorage.setItem(QKEY, JSON.stringify(q));

const doStatus = (sid, status, note) =>
  api.post(`/driver/jobs/${sid}/status?status=${encodeURIComponent(status)}&note=${encodeURIComponent(note || "")}`);
const doPod = (sid, payload) => api.post(`/driver/jobs/${sid}/pod`, payload);

function QrDialog({ job, onVerified }) {
  const [open, setOpen] = useState(false);
  const handle = useCallback((text) => {
    const code = (text || "").trim().toUpperCase();
    if (code === job.tracking_number.toUpperCase()) {
      toast.success(`Cargo verified · ${job.tracking_number}`);
      onVerified(); setOpen(false);
    } else {
      toast.error(`Scanned ${code} — does not match ${job.tracking_number}`);
    }
  }, [job, onVerified]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-start" data-testid="job-scan"><ScanLine size={16} className="mr-2 text-primary" /> Scan cargo QR / barcode</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Scan cargo · {job.tracking_number}</DialogTitle><DialogDescription>Scan the cargo QR/barcode, or enter the code manually to verify this consignment.</DialogDescription></DialogHeader>
        {open && <QrScanner onResult={handle} />}
      </DialogContent>
    </Dialog>
  );
}

function PODDialog({ job, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({ recipient: "", notes: "Received in good condition", quantity_delivered: 1, signature: "", photo_urls: [] });
  const fileRef = useRef(null);
  const token = localStorage.getItem("ebik_token");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const pickPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!navigator.onLine) { toast.error("Photo upload needs a connection"); return; }
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/driver/pod/photo?shipment_id=${job.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setF((p) => ({ ...p, photo_urls: [...p.photo_urls, r.data.path] }));
        toast.success("Photo uploaded");
      } catch { toast.error("Photo upload failed"); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!f.recipient) return toast.error("Recipient name required");
    if (!f.signature) return toast.error("Signature required");
    await onSubmit(job, { recipient: f.recipient, notes: f.notes, quantity_delivered: Number(f.quantity_delivered), signature: f.signature, photo_urls: f.photo_urls });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="pod-open"><CheckCircle2 size={16} className="mr-2" /> Complete Delivery (e-POD)</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Digital Proof of Delivery</DialogTitle><DialogDescription>Capture recipient details, a photo and signature to complete this delivery.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Recipient name</Label><Input data-testid="pod-recipient" value={f.recipient} onChange={(e) => setF({ ...f, recipient: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Quantity delivered</Label><Input data-testid="pod-qty" type="number" value={f.quantity_delivered} onChange={(e) => setF({ ...f, quantity_delivered: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" /></div>
          <div>
            <Label>Delivery photos</Label>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={pickPhoto} data-testid="pod-photo-input" />
            <Button variant="outline" className="w-full mt-1.5" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pod-photo"><Camera size={16} className="mr-2" /> {uploading ? "Uploading…" : "Capture / attach photo"}</Button>
            {f.photo_urls.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap" data-testid="pod-photo-thumbs">
                {f.photo_urls.map((p, i) => (
                  <img key={i} src={`${BACKEND}/api/files/${p}?auth=${token}`} alt="delivery proof" className="w-16 h-16 object-cover rounded-md border border-border" />
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Recipient signature</Label>
            <div className="mt-1.5"><SignaturePad onChange={(sig) => setF((p) => ({ ...p, signature: sig }))} /></div>
          </div>
        </div>
        <DialogFooter><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submit} data-testid="pod-submit">Confirm Delivery</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Driver() {
  const { data, loading, setData } = useFetch(() => api.get("/driver/jobs").then((r) => r.data));
  const [active, setActive] = useState(null);
  const [gpsOn, setGpsOn] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(readQ().length);

  const reload = async () => { const r = await api.get("/driver/jobs"); setData(r.data); setActive(null); };

  const flush = useCallback(async () => {
    let q = readQ();
    if (!q.length || !navigator.onLine) return;
    const remaining = [];
    for (const item of q) {
      try {
        if (item.type === "status") await doStatus(item.sid, item.status, item.note);
        else if (item.type === "pod") await doPod(item.sid, item.payload);
      } catch { remaining.push(item); }
    }
    writeQ(remaining); setPending(remaining.length);
    if (remaining.length < q.length) { toast.success(`Synced ${q.length - remaining.length} offline action(s)`); reload(); }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const on = () => { setOnline(true); flush(); };
    const off = () => { setOnline(false); toast.info("Offline — actions will be queued & synced later"); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    if (navigator.onLine) flush();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [flush]);

  if (loading) return <Spinner />;

  const enqueue = (item) => { const q = readQ(); q.push(item); writeQ(q); setPending(q.length); };

  const setStatus = async (job, status, note) => {
    if (!navigator.onLine) { enqueue({ type: "status", sid: job.id, status, note, tn: job.tracking_number }); toast.success(`Queued offline: ${status}`); return; }
    try { await doStatus(job.id, status, note); toast.success(`Status updated: ${status}`); reload(); }
    catch { enqueue({ type: "status", sid: job.id, status, note, tn: job.tracking_number }); toast.warning("Saved offline — will sync"); }
  };

  const submitPod = async (job, payload) => {
    if (!navigator.onLine) { enqueue({ type: "pod", sid: job.id, payload, tn: job.tracking_number }); toast.success("POD queued offline"); reload(); return; }
    try { await doPod(job.id, payload); toast.success("Delivery completed · POD captured"); reload(); }
    catch { enqueue({ type: "pod", sid: job.id, payload, tn: job.tracking_number }); toast.warning("POD saved offline — will sync"); }
  };

  const toggleGps = () => {
    if (gpsOn) {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      setWatchId(null); setGpsOn(false); toast.info("Live GPS sharing stopped"); return;
    }
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    const id = navigator.geolocation.watchPosition(
      async (pos) => { try { await api.post("/driver/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }); } catch { /* */ } },
      () => toast.error("Location permission denied"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    setWatchId(id); setGpsOn(true); toast.success("Sharing live GPS with the control tower");
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="rounded-[2.5rem] border-8 border-[#0B0F17] bg-[#0B0F17] shadow-2xl overflow-hidden">
        <div className="bg-primary text-white px-5 py-4 flex items-center gap-3">
          <Smartphone size={22} />
          <div className="flex-1"><h1 className="font-heading font-extrabold">Ebik Driver</h1><p className="text-xs text-white/80">Assigned jobs · {data.jobs.length}</p></div>
          <button onClick={toggleGps} data-testid="driver-gps-toggle"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${gpsOn ? "bg-white text-primary" : "bg-white/20 text-white"}`}>
            <Navigation size={13} /> {gpsOn ? "GPS ON" : "Share GPS"}
          </button>
        </div>

        {/* Connectivity bar */}
        <div className={`px-5 py-2 flex items-center justify-between text-xs font-semibold ${online ? "bg-emerald-500/15 text-emerald-300" : "bg-orange-500/20 text-orange-300"}`} data-testid="driver-connectivity">
          <span className="flex items-center gap-1.5">{online ? <Wifi size={13} /> : <WifiOff size={13} />} {online ? "Online" : "Offline mode"}</span>
          {pending > 0 && (
            <button onClick={flush} disabled={!online} className="flex items-center gap-1.5 disabled:opacity-50" data-testid="driver-sync-btn">
              <RefreshCw size={12} /> {pending} queued · Sync now
            </button>
          )}
          {pending === 0 && online && <span className="flex items-center gap-1 text-emerald-300"><Check size={12} /> Synced</span>}
        </div>

        <div className="bg-background max-h-[620px] overflow-y-auto scrollbar-thin p-4 space-y-4">
          {data.jobs.length === 0 && <p className="text-center text-muted-foreground py-10 text-sm">No jobs assigned.</p>}
          {data.jobs.map((job) => (
            <div key={job.id} className="rounded-xl border border-border bg-card p-4" data-testid={`job-${job.tracking_number}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-sm">{job.tracking_number}</span>
                <StatusBadge status={job.status} />
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                <p className="flex items-center gap-2"><MapPin size={14} className="text-emerald-600" /> Pickup: <b>{job.origin}</b></p>
                <p className="flex items-center gap-2"><MapPin size={14} className="text-primary" /> Deliver: <b>{job.destination}</b></p>
                <p className="flex items-center gap-2 text-muted-foreground"><Package size={14} /> {job.cargo_type} · {job.weight_kg} kg</p>
              </div>

              {active === job.id ? (
                <div className="mt-4 space-y-2">
                  <Button variant="outline" className="w-full justify-start" onClick={() => toast.success("Navigation started")} data-testid="job-navigate"><Navigation size={16} className="mr-2 text-blue-600" /> Navigate to pickup</Button>
                  <QrDialog job={job} onVerified={() => setStatus(job, "In Transit", "Cargo scanned & verified")} />
                  <Button variant="outline" className="w-full justify-start" onClick={() => setStatus(job, "In Transit", "Departed pickup")} data-testid="job-pickup"><Truck size={16} className="mr-2 text-emerald-600" /> Confirm pickup & depart</Button>
                  <Button variant="outline" className="w-full justify-start text-orange-600" onClick={() => setStatus(job, "Delayed", "Delay reported by driver")} data-testid="job-delay"><AlertTriangle size={16} className="mr-2" /> Report delay / incident</Button>
                  <PODDialog job={job} onSubmit={submitPod} />
                </div>
              ) : (
                <Button className="mt-4 w-full bg-primary hover:bg-red-700" onClick={() => setActive(job.id)} data-testid="job-accept">
                  Open Job <ArrowRight size={16} className="ml-2" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
